/**
 * Class representing a theme switcher for a website.
 */
class ThemeSwitcher {

	constructor() {
		this.html = document.querySelector('html');
		this.mode = localStorage.getItem('mode') || 'auto';
		this.SYSLIGHT = 'sys-light';
		this.SYSDARK = 'sys-dark';
		this.modeElements = {
			autoSwitch: 'switchAuto',
			lightSwitch: 'switchLight',
			darkSwitch: 'switchDark',
		};

		this.init();
	}

	/**
	 * Initialize the theme switcher by setting the initial color scheme,
	 * adding system color scheme class, and listening for system color scheme changes.
	 */
	init() {
		this.setInitialColorScheme();
		this.addSystemColorSchemeClass();
		this.listenForSystemColorSchemeChanges();

		document.addEventListener('DOMContentLoaded', () => {
			this.elementAuto  = document.getElementById('autoSwitch') || null;
			this.elementLight = document.getElementById('lightSwitch') || null;
			this.elementDark  = document.getElementById('darkSwitch') || null;
			this.setIconColors(this.mode);
			this.setClickHandler();
		});
	}

	/**
	 * Check if an element exists in the DOM.
	 * @param {string} element - The ID of the element to check.
	 * @returns {boolean} True if the element exists, false otherwise.
	 */
	elementExists(element) {
		return null !== document.getElementById(element);
	}

	/**
	 * Set the aria-pressed attribute for the mode switch buttons.
	 * @param {...boolean} args - The boolean values to set for each mode switch button.
	 */
	ariaPressed(...args) {
		if (this.elementExists('autoSwitch')) {
			const elements = Object.keys(this.modeElements);
			args.forEach((boolean, i) => {
				document.getElementById(elements[i]).setAttribute('aria-pressed', boolean);
			});
		}
	}

	/**
	 * Switch the HTML class for the color scheme.
	 * @param {string} param - The color scheme to switch to.
	 */
	switchHTMLClass(param) {
		const classname = param.replace(/ /g, '-');
		const list = this.html.classList;
		[...list].forEach(item => {
			if (item.startsWith("scheme-")) {
				this.html.classList.remove(item);
			}
		});
		this.html.classList.add('scheme-' + classname);
	}

	/**
	 * Switch to auto color scheme.
	 */
	switchAuto() {
		this.switchHTMLClass('light dark');
		localStorage.removeItem('mode');
		this.ariaPressed(false, false, true);
		try {
			this.setElementProperties(this.elementAuto, 'white', '700');
			this.setElementProperties(this.elementLight, 'var(--cnf-medium-grey)', '400');
			this.setElementProperties(this.elementDark, 'var(--cnf-medium-grey)', '400');
		} catch (error) {}
	}

	/**
	 * Switch to light color scheme.
	 */
	switchLight() {
		this.switchHTMLClass('light');
		localStorage.setItem('mode', 'light');
		this.ariaPressed(true, false, false);
		try {
			this.setElementProperties(this.elementAuto, 'var(--cnf-medium-grey)', '400');
			this.setElementProperties(this.elementLight, 'white', '700');
			this.setElementProperties(this.elementDark, 'var(--cnf-medium-grey)', '400');
		} catch (error) {}

	}

	/**
	 * Switch to dark color scheme.
	 */
	switchDark() {
		this.switchHTMLClass('dark');
		localStorage.setItem('mode', 'dark');
		this.ariaPressed(false, true, false);
		try {
			this.setElementProperties(this.elementAuto, 'var(--cnf-medium-grey)', '400');
			this.setElementProperties(this.elementLight, 'var(--cnf-medium-grey)', '400');
			this.setElementProperties(this.elementDark, 'white', '700');
		} catch (error) {}
	}

	/**
	 * Set the initial color scheme based on the saved mode in localStorage.
	 */
	setInitialColorScheme() {
		if (this.mode === 'light') this.switchLight();
		if (this.mode === 'dark') this.switchDark();
		if (this.mode === 'auto') this.switchAuto();
	}

	/**
	 * Add the system color scheme class to the HTML element.
	 */
	addSystemColorSchemeClass() {
		(window.matchMedia('(prefers-color-scheme: dark)').matches)
			? this.html.classList.add(this.SYSDARK)
			: this.html.classList.add(this.SYSLIGHT);
	}

	/**
	 * Listen for changes in the system color scheme and update the HTML class accordingly.
	 */
	listenForSystemColorSchemeChanges() {
		window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (event) => {
			const theme = event.matches ? 'dark' : 'light';
			if (theme === 'dark') {
				this.html.classList.remove(this.SYSLIGHT);
				this.html.classList.add(this.SYSDARK);
			}
			if (theme === 'light') {
				this.html.classList.remove(this.SYSDARK);
				this.html.classList.add(this.SYSLIGHT);
			}
		});
	}

	/**
	 * Sets the icon colors based on the current mode.
	 * @param {string} currentMode - The current mode (light, dark, or auto).
	 */
	setIconColors(currentMode) {
		switch (currentMode) {
			case 'light':
				this.setElementProperties(this.elementLight, 'white', '700'); break;
			case 'dark':
				this.setElementProperties(this.elementDark, 'white', '700'); break;
			case 'auto':
				this.setElementProperties(this.elementAuto, 'white', '700'); break;
			default:
				this.setElementProperties(this.elementLight, 'white', '700'); break;
		}
	}

	setElementProperties(element, theme, weight) {
		const mode = element.getAttribute('id').replace(/Switch$/, '');
		element.style.setProperty(`--theme-${mode}`, theme);
		element.style.setProperty('font-weight', weight);
	}
	/**
	 * Set click handlers for the mode switch buttons.
	 */
	setClickHandler() {
		Object.keys(this.modeElements).forEach((element) => {
			document.getElementById(element).addEventListener("click", () => {
				this[this.modeElements[element]]();
			});
		});
	}
}

// Instantiate the ThemeSwitcher class
const themeSwitcher = new ThemeSwitcher();
